import { useCallback, useEffect, useState } from "react";
import { api } from "../../api";
import { ErrorNote, Loading } from "../../components/Feedback";
import { Badge, Btn, Card, Field, Input } from "../../components/Shared";
import { SwitchRow, formatWhen } from "../admin/parts";

// The log (task 2026-10-20 "each project has a log, and admins see the logs of all projects").
// One component for a project's Log page and Management -> Log:
//   * Portal actions: GET <base> (a project's: /projects/{id}/log; the admin's: /admin/log), with
//     the filters person, action, from, to (and, for the admin, project), "Load older" paging by
//     `before`, Export (TSV with the same filters) and the switch that shows the sign-ins and
//     read-only actions (off by default);
//   * Data changes (a hosted project only): GET /projects/{id}/log/data-changes, read under the
//     viewer's own data role, so a column marked personal data reads *** without the permission.
// Nobody deletes an entry from here.

export function buildQuery(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "" && v !== false) q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

function short(v) {
  if (v === null || v === undefined) return "";
  return typeof v === "string" ? v : JSON.stringify(v);
}

function ActionFilters({ filters, setFilters, withProject, projects }) {
  const set = k => e => setFilters(f => ({ ...f, [k]: e.target.value }));
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 12 }}>
      {withProject && (
        <Field label="Project">
          <select value={filters.project} onChange={set("project")}
                  style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}>
            <option value="">All projects and portal-wide</option>
            <option value="none">Portal-wide only</option>
            {(projects || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      )}
      <Field label="Person"><Input value={filters.user} onChange={set("user")} placeholder="user name" /></Field>
      <Field label="Action"><Input value={filters.action} onChange={set("action")} placeholder="e.g. project. or data-import.approve" /></Field>
      <Field label="From"><Input type="date" value={filters.from} onChange={set("from")} /></Field>
      <Field label="To (exclusive)"><Input type="date" value={filters.to} onChange={set("to")} /></Field>
    </div>
  );
}

// A typed filter asks the server once the typing pauses, not on every key.
export const FILTER_DELAY_MS = 300;

function useDebounced(value, delay) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    if (value === settled) return undefined;
    const t = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(t);
  }, [value, settled, delay]);
  return settled;
}

function useLog(path, params) {
  const [entries, setEntries] = useState([]);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const query = useDebounced(buildQuery(params), FILTER_DELAY_MS);
  const load = useCallback(async (before) => {
    setLoading(true);
    setError(null);
    try {
      const sep = query ? "&" : "?";
      const r = await api.get(`${path}${query}${before ? `${sep}before=${before}` : ""}`);
      const rows = r.entries || r.changes || [];
      setEntries(e => (before ? [...e, ...rows] : rows));
      setNext(r.next_before || null);
      return r;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [path, query]);
  useEffect(() => { load(null); }, [load]);
  return { entries, next, loading, error, more: () => load(next), query };
}

function PortalActions({ base, withProject, projects, exportName }) {
  const [filters, setFilters] = useState({ project: "", user: "", action: "", from: "", to: "" });
  const [all, setAll] = useState(false);
  const log = useLog(base, { ...filters, all: all ? "true" : "" });
  return (
    <section aria-label="Portal actions">
      <ActionFilters filters={filters} setFilters={setFilters} withProject={withProject} projects={projects} />
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 12 }}>
        <SwitchRow label="Show sign-ins and read-only actions" checked={all} onChange={setAll}
                   hint="Sign-ins, sign-outs, refused sign-ins and reads of personal data are hidden by default." />
        <Btn variant="secondary" onClick={() => api.download(`${base}/export.tsv${log.query}`, exportName)}>Export TSV</Btn>
      </div>
      <ErrorNote error={log.error} />
      {log.entries.length === 0 ? (
        !log.loading && <p style={{ fontSize: 14, color: "#6b7280" }}>No entries match.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="responsive-table" aria-label="Portal actions" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>
                <th scope="col" style={{ padding: 6 }}>When</th>
                {withProject && <th scope="col" style={{ padding: 6 }}>Project</th>}
                <th scope="col" style={{ padding: 6 }}>Who</th>
                <th scope="col" style={{ padding: 6 }}>Action</th>
                <th scope="col" style={{ padding: 6 }}>Target</th>
                <th scope="col" style={{ padding: 6 }}>What</th>
              </tr>
            </thead>
            <tbody>
              {log.entries.map(e => (
                <tr key={e.id} style={{ borderBottom: "1px solid #f3f4f6", verticalAlign: "top" }}>
                  <td data-label="When" style={{ padding: 6, whiteSpace: "nowrap" }}>{formatWhen(e.at)}</td>
                  {withProject && <td data-label="Project" style={{ padding: 6 }}>{e.project_slug || (e.project_id == null ? "—" : `#${e.project_id}`)}</td>}
                  <td data-label="Who" style={{ padding: 6 }}>{e.actor}{e.via !== "api" ? ` (${e.via})` : ""}</td>
                  <td data-label="Action" style={{ padding: 6 }}>
                    {e.level === "warning" ? <Badge color="amber">{e.action}</Badge> : <code>{e.action}</code>}
                  </td>
                  <td data-label="Target" style={{ padding: 6, wordBreak: "break-word" }}>{e.target}</td>
                  <td data-label="What" style={{ padding: 6, wordBreak: "break-word", color: "#4b5563" }}>
                    {[...(e.fields || []), ...Object.entries(e.detail || {}).map(([k, v]) => `${k}: ${short(v)}`)].join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {log.loading && <Loading />}
      {log.next && !log.loading && <Btn variant="secondary" onClick={log.more} style={{ marginTop: 10 }}>Load older entries</Btn>}
    </section>
  );
}

function DataChanges({ projectId, slug }) {
  const [filters, setFilters] = useState({ table: "", op: "", user: "", from: "", to: "" });
  const base = `/projects/${projectId}/log/data-changes`;
  const log = useLog(base, filters);
  const set = k => e => setFilters(f => ({ ...f, [k]: e.target.value }));
  return (
    <section aria-label="Data changes">
      <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 10px" }}>
        Every row created, changed or deleted in the project database, as your data role reads it:
        columns marked as personal data show *** unless you may read personal data.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 12 }}>
        <Field label="Table"><Input value={filters.table} onChange={set("table")} /></Field>
        <Field label="Operation">
          <select value={filters.op} onChange={set("op")}
                  style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}>
            <option value="">Any</option>
            {["insert", "update", "delete", "split", "merge"].map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Person or login"><Input value={filters.user} onChange={set("user")} /></Field>
        <Field label="From"><Input type="date" value={filters.from} onChange={set("from")} /></Field>
        <Field label="To (exclusive)"><Input type="date" value={filters.to} onChange={set("to")} /></Field>
      </div>
      <Btn variant="secondary" onClick={() => api.download(`${base}/export.tsv${log.query}`, `data-changes-${slug}.tsv`)}>Export TSV</Btn>
      <ErrorNote error={log.error} />
      {log.entries.length === 0 ? (
        !log.loading && <p style={{ fontSize: 14, color: "#6b7280" }}>No data changes match.</p>
      ) : (
        <div style={{ overflowX: "auto", marginTop: 10 }}>
          <table className="responsive-table" aria-label="Data changes" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>
                <th scope="col" style={{ padding: 6 }}>When</th>
                <th scope="col" style={{ padding: 6 }}>Table</th>
                <th scope="col" style={{ padding: 6 }}>Operation</th>
                <th scope="col" style={{ padding: 6 }}>Who</th>
                <th scope="col" style={{ padding: 6 }}>Key</th>
                <th scope="col" style={{ padding: 6 }}>Before</th>
                <th scope="col" style={{ padding: 6 }}>After</th>
              </tr>
            </thead>
            <tbody>
              {log.entries.map(c => (
                <tr key={c.id} style={{ borderBottom: "1px solid #f3f4f6", verticalAlign: "top" }}>
                  <td data-label="When" style={{ padding: 6, whiteSpace: "nowrap" }}>{formatWhen(c.at)}</td>
                  <td data-label="Table" style={{ padding: 6 }}><code>{c.table}</code></td>
                  <td data-label="Operation" style={{ padding: 6 }}>{c.op}</td>
                  <td data-label="Who" style={{ padding: 6 }}>{c.user || c.db_role}{c.import_id ? ` (import ${c.import_id})` : ""}</td>
                  <td data-label="Key" style={{ padding: 6, wordBreak: "break-word" }}>{short(c.pk)}</td>
                  <td data-label="Before" style={{ padding: 6, wordBreak: "break-word", fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{short(c.old)}</td>
                  <td data-label="After" style={{ padding: 6, wordBreak: "break-word", fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{short(c.new)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {log.loading && <Loading />}
      {log.next && !log.loading && <Btn variant="secondary" onClick={log.more} style={{ marginTop: 10 }}>Load older changes</Btn>}
    </section>
  );
}

function ViewTabs({ view, setView, hosted }) {
  const tabs = [{ key: "actions", label: "Portal actions" }, ...(hosted ? [{ key: "data", label: "Data changes" }] : [])];
  return (
    <div role="tablist" aria-label="Log views" style={{ display: "flex", gap: 6, marginBottom: 14, borderBottom: "1px solid #e5e7eb" }}>
      {tabs.map(t => (
        <button key={t.key} role="tab" type="button" aria-selected={view === t.key} onClick={() => setView(t.key)}
                style={{ padding: "8px 14px", fontSize: 14, background: "none", border: "none", cursor: "pointer",
                         fontWeight: view === t.key ? 700 : 500, color: view === t.key ? "var(--dp-accent-dark)" : "#374151",
                         borderBottom: view === t.key ? "3px solid var(--dp-accent-dark)" : "3px solid transparent", marginBottom: -1 }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** A project's log: its portal actions and, when hosted, its data changes. */
export function ProjectLog({ project }) {
  const [view, setView] = useState("actions");
  const hosted = project.hosted_version != null && !["cold", "restoring"].includes(project.archive_state);
  return (
    <Card>
      <ViewTabs view={view} setView={setView} hosted={hosted} />
      {view === "actions"
        ? <PortalActions base={`/projects/${project.id}/log`} exportName={`log-${project.slug}.tsv`} />
        : <DataChanges projectId={project.id} slug={project.slug} />}
    </Card>
  );
}

/** Management -> Log: every project's actions and the portal-wide ones, Verify, retention. */
export function AdminLog() {
  const [projects, setProjects] = useState([]);
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [days, setDays] = useState("");
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    api.get("/projects").then(setProjects).catch(() => setProjects([]));
    api.get("/admin/log/settings").then(s => setDays(String(s.retention_days))).catch(setError);
  }, []);

  const runVerify = async () => {
    setVerifying(true);
    setError(null);
    try { setVerify(await api.get("/admin/log/verify")); } catch (err) { setError(err); } finally { setVerifying(false); }
  };
  const save = async e => {
    e.preventDefault();
    setError(null);
    setSaved(null);
    try {
      const s = await api.put("/admin/log/settings", { retention_days: Number(days || 0) });
      setDays(String(s.retention_days));
      setSaved(s.retention_days);
    } catch (err) { setError(err); }
  };

  return (
    <>
      <Card style={{ marginBottom: 18 }}>
        <h2 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700 }}>Integrity and retention</h2>
        <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 12px" }}>
          Entries are chained by their hashes: an entry edited or deleted directly in the database
          makes Verify name it. Nobody deletes entries from the portal; entries older than the
          retention are pruned once a day, and a checkpoint entry keeps the chain verifiable.
        </p>
        <ErrorNote error={error} />
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Btn onClick={runVerify} disabled={verifying}>{verifying ? "Verifying…" : "Verify"}</Btn>
          <form onSubmit={save} aria-label="Log retention" style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <Field label="Keep entries for (days, 0 = forever)" hint="0, or at least 30 days. SQL-login entries are always kept.">
              <Input type="number" min="0" value={days} onChange={e => setDays(e.target.value)} />
            </Field>
            <Btn type="submit" variant="secondary">Save retention</Btn>
          </form>
        </div>
        {saved !== null && <p role="status" style={{ fontSize: 13, color: "#065f46" }}>
          {saved === 0 ? "Entries are kept forever." : `Entries older than ${saved} days are pruned.`}</p>}
        {verify && (
          <div role="status" data-testid="verify-result" style={{ marginTop: 12, fontSize: 14 }}>
            {verify.ok
              ? <span><Badge color="green">intact</Badge> {verify.checked} entries checked.</span>
              : (
                <>
                  <span><Badge color="red">changed</Badge> {verify.problems.length} problem{verify.problems.length === 1 ? "" : "s"} in {verify.checked} entries:</span>
                  <ul style={{ margin: "6px 0 0" }}>
                    {verify.problems.map(p => <li key={`${p.id}-${p.problem}`}>Entry {p.id} ({p.action}): {p.problem}</li>)}
                  </ul>
                </>
              )}
          </div>
        )}
      </Card>
      <Card>
        <PortalActions base="/admin/log" withProject projects={projects} exportName="log.tsv" />
      </Card>
    </>
  );
}
